import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { User, Lock, Eye, EyeOff } from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  // Login Form State
  const [loginData, setLoginData] = useState({
    username: '',
    password: ''
  });
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);

    const result = await login(loginData.username, loginData.password, rememberMe);

    if (result.success) {
      const userStr = sessionStorage.getItem('user');
      if (userStr) {
        const userData = JSON.parse(userStr);
        if (userData.role === 'auditor') {
          navigate('/auditor');
        } else {
          navigate('/dashboard');
        }
      } else {
        navigate('/dashboard');
      }
    } else {
      setLoginError(result.error);
    }

    setLoginLoading(false);
  };

  // Container variants for stagger animation
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1
      }
    }
  };

  // Child variants for stagger
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const styles = `
    .login-page {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background: hsl(var(--background));
      padding: var(--page-padding);
      position: relative;
      overflow: hidden;
    }

    /* Ambient Background Blobs */
    .ambient-blob {
      position: absolute;
      border-radius: 50%;
      filter: blur(80px);
      opacity: 0.15;
      animation: float 8s ease-in-out infinite;
      pointer-events: none;
    }

    .blob-1 {
      width: 400px;
      height: 400px;
      background: hsl(var(--primary));
      top: -100px;
      left: -100px;
      animation-delay: 0s;
    }

    .blob-2 {
      width: 500px;
      height: 500px;
      background: hsl(var(--primary-glow));
      bottom: -150px;
      right: -150px;
      animation-delay: 2s;
    }

    .blob-3 {
      width: 350px;
      height: 350px;
      background: hsl(var(--info));
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      animation-delay: 4s;
      opacity: 0.08;
    }

    .login-card {
      position: relative;
      z-index: 10;
      width: 100%;
      max-width: 420px;
      padding: 40px;
      border-radius: var(--radius-lg);
      background: hsl(0 0% 100% / 0.7);
      backdrop-filter: blur(16px);
      -webkit-backdrop-filter: blur(16px);
      border: 1px solid hsl(var(--border));
      box-shadow: var(--shadow-lg);
    }

    .logo-container {
      display: flex;
      justify-content: center;
      margin-bottom: 32px;
    }

    .logo {
      width: 64px;
      height: 64px;
      border-radius: var(--radius-md);
      background: var(--gradient-primary);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 32px;
      font-weight: 800;
      color: white;
      box-shadow: var(--shadow-glow);
      position: relative;
      overflow: hidden;
    }

    .welcome-text {
      text-align: center;
      margin-bottom: 32px;
    }

    .welcome-text h1 {
      font-size: 28px;
      font-weight: 800;
      color: hsl(var(--foreground));
      margin-bottom: 8px;
    }

    .welcome-text p {
      font-size: 14px;
      color: hsl(var(--muted-foreground));
    }

    .form-group {
      margin-bottom: 20px;
    }

    .form-label {
      display: block;
      font-size: 13px;
      font-weight: 600;
      color: hsl(var(--foreground));
      margin-bottom: 8px;
    }

    .input-wrapper {
      position: relative;
    }

    .input-icon {
      position: absolute;
      left: 14px;
      top: 50%;
      transform: translateY(-50%);
      color: hsl(var(--muted-foreground));
      width: 18px;
      height: 18px;
    }

    .input-field {
      width: 100%;
      padding: 12px 14px 12px 42px;
      border-radius: var(--radius-md);
      border: 1px solid hsl(var(--border));
      background: hsl(var(--card));
      font-size: 14px;
      font-family: var(--font-primary);
      color: hsl(var(--foreground));
      transition: all 0.2s;
    }

    .input-field:focus {
      outline: none;
      border-color: hsl(var(--ring));
      box-shadow: 0 0 0 3px hsl(var(--ring) / 0.1);
    }

    .input-field::placeholder {
      color: hsl(var(--muted-foreground) / 0.5);
    }

    .password-toggle {
      position: absolute;
      right: 14px;
      top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      cursor: pointer;
      color: hsl(var(--muted-foreground));
      padding: 4px;
      display: flex;
      align-items: center;
      transition: color 0.2s;
    }

    .password-toggle:hover {
      color: hsl(var(--foreground));
    }

    .form-footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 24px;
    }

    .remember-me {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 13px;
      color: hsl(var(--foreground));
      cursor: pointer;
    }

    .remember-me input[type="checkbox"] {
      width: 16px;
      height: 16px;
      cursor: pointer;
      accent-color: hsl(var(--primary));
    }

    .submit-button {
      width: 100%;
      padding: 12px 24px;
      border-radius: var(--radius-md);
      border: none;
      background: var(--gradient-primary);
      color: white;
      font-size: 14px;
      font-weight: 600;
      font-family: var(--font-primary);
      cursor: pointer;
      position: relative;
      overflow: hidden;
      box-shadow: var(--shadow-glow);
      transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
    }

    .submit-button:hover:not(:disabled) {
      transform: scale(1.02);
    }

    .submit-button:active:not(:disabled) {
      transform: scale(0.97);
    }

    .submit-button:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .error-alert {
      padding: 12px 16px;
      border-radius: var(--radius-md);
      background: hsl(var(--destructive) / 0.1);
      border: 1px solid hsl(var(--destructive) / 0.3);
      color: hsl(var(--destructive));
      font-size: 13px;
      margin-bottom: 20px;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .contact-footer {
      text-align: center;
      margin-top: 24px;
      font-size: 13px;
      color: hsl(var(--muted-foreground));
    }

    .contact-link {
      color: hsl(var(--primary));
      background: none;
      border: none;
      padding: 0;
      font-weight: 600;
      transition: opacity 0.2s;
      cursor: pointer;
      font-family: inherit;
    }

    .contact-link:hover {
      opacity: 0.8;
    }

    @keyframes float {
      0%, 100% {
        transform: translateY(0px) scale(1);
      }
      50% {
        transform: translateY(-20px) scale(1.05);
      }
    }

    @media (max-width: 480px) {
      .login-card {
        padding: 32px 24px;
      }

      .welcome-text h1 {
        font-size: 24px;
      }
    }
  `;

  return (
    <>
      <style>{styles}</style>
      <div className="login-page">
        {/* Ambient Background Blobs */}
        <div className="ambient-blob blob-1"></div>
        <div className="ambient-blob blob-2"></div>
        <div className="ambient-blob blob-3"></div>

        {/* Login Card */}
        <motion.div
          className="login-card"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            duration: 0.5,
            ease: [0.22, 1, 0.36, 1]
          }}
        >
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Logo */}
            <motion.div variants={itemVariants} className="logo-container">
              <div className="logo shimmer-overlay">
                P
              </div>
            </motion.div>

            {/* Welcome Text */}
            <motion.div variants={itemVariants} className="welcome-text">
              <h1>Welcome back</h1>
              <p>Sign in to your PixelAudit dashboard</p>
            </motion.div>

            {/* Login Form */}
            <form onSubmit={handleLoginSubmit}>
              {/* Error Message */}
              {loginError && (
                <motion.div
                  variants={itemVariants}
                  className="error-alert"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <span>⚠️</span>
                  {loginError}
                </motion.div>
              )}

              {/* Username Field */}
              <motion.div variants={itemVariants} className="form-group">
                <label className="form-label">Username</label>
                <div className="input-wrapper">
                  <User className="input-icon" size={18} />
                  <input
                    type="text"
                    className="input-field"
                    placeholder="Enter your username"
                    value={loginData.username}
                    onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                    required
                  />
                </div>
              </motion.div>

              {/* Password Field */}
              <motion.div variants={itemVariants} className="form-group">
                <label className="form-label">Password</label>
                <div className="input-wrapper">
                  <Lock className="input-icon" size={18} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="input-field"
                    placeholder="••••••••"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </motion.div>

              {/* Remember Me */}
              <motion.div variants={itemVariants} className="form-footer">
                <label className="remember-me">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  Remember me
                </label>
              </motion.div>

              {/* Submit Button */}
              <motion.div variants={itemVariants}>
                <button
                  type="submit"
                  className="submit-button shimmer-overlay"
                  disabled={loginLoading}
                >
                  {loginLoading ? 'Signing in...' : 'Sign in'}
                  {!loginLoading && ' →'}
                </button>
              </motion.div>
            </form>

          </motion.div>
        </motion.div>
      </div>
    </>
  );
};

export default Login;
